/**
 * orchestratorDecisionLog — client-side fire-and-forget poster to the
 * orchestratorDecisionLog Cloud Function (which stays live — Cloud Logging
 * indexes decisions and turn errors by accountId for per-account analysis).
 *
 * Lifted verbatim from services/globalTools/orchestrator/index.ts on
 * 2026-08-25 (D13-6b): the in-browser orchestrator brain was severed
 * (D13-6a, zero call sites) and its directory deleted, but this
 * observability leaf is still used by the live prest0n-session lane's
 * error path (useGlobalTools). The module name matches the Cloud Function
 * it posts to. Never blocks, never throws — observability only.
 */

function getOrchestratorDecisionLogUrl(): string {
  const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'tensile-tenure-453409-k3';
  return `https://us-central1-${projectId}.cloudfunctions.net/orchestratorDecisionLog`;
}

function getAccountHintForOrchestrator(): string {
  try {
    const raw = localStorage.getItem('user_accounts');
    if (raw) {
      const accounts = JSON.parse(raw);
      if (Array.isArray(accounts) && accounts.length > 0 && accounts[0].accountId) {
        return accounts[0].accountId;
      }
    }
    const pathPart = window.location.pathname.split('/')[1];
    return pathPart || 'unknown';
  } catch {
    return 'unknown';
  }
}

async function logOrchestratorDecision(data: {
  decisionType: string;
  workflowIds: string[];
  confidence: number;
  reasoning: string;
  hasAttachments: boolean;
  attachmentCount: number;
  messageLength: number;
  documentContentLength: number;
  conversationalResponseLength: number;
  model?: string;
  responseTimeMs?: number;
}): Promise<void> {
  try {
    const accountHint = getAccountHintForOrchestrator();
    await fetch(getOrchestratorDecisionLogUrl(), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Account-Id': accountHint,
      },
      body: JSON.stringify(data),
      // Use keepalive so this completes even if the tab closes mid-turn
      keepalive: true,
    });
  } catch (e) {
    // Observability-only: never surface failures to the caller.
    console.warn('[Orchestrator] Decision log post failed (non-fatal):', e);
  }
}

/**
 * Server-side log for a FAILED turn (2026-08-18 RCA). The generic
 * "Something went wrong" bubble used to discard the error entirely — every
 * failure logged NOTHING server-side, so production failures were
 * undiagnosable. Fire-and-forget; never throws.
 */
export function logOrchestratorTurnError(error: unknown, phase: string): void {
  try {
    const err = error instanceof Error ? error : new Error(String(error));
    void logOrchestratorDecision({
      decisionType: 'error',
      workflowIds: [],
      confidence: 0,
      reasoning: `${phase}: ${err.name}: ${err.message}`.slice(0, 900),
      hasAttachments: false,
      attachmentCount: 0,
      messageLength: 0,
      documentContentLength: 0,
      conversationalResponseLength: 0,
    });
  } catch {
    /* observability only */
  }
}
